import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  FileText, Plus, Upload, Trash2, ExternalLink, MoreVertical,
  LogOut, FileOutput, BookOpen, LayoutDashboard, Clock
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";

export default function DashboardPage() {
  const { authHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates`, authHeaders());
      setTemplates(res.data);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("name", uploadName.trim());
      formData.append("file", uploadFile);
      await axios.post(`${API}/templates`, formData, {
        headers: {
          ...authHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Template uploaded successfully");
      setUploadOpen(false);
      setUploadName("");
      setUploadFile(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/templates/${id}`, authHeaders());
      toast.success("Template deleted");
      fetchTemplates();
    } catch {
      toast.error("Failed to delete template");
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 lg:p-8" data-testid="dashboard-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
              Templates
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Upload and manage your PDF templates
            </p>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-template-button" className="gap-2">
                <Plus size={16} strokeWidth={1.5} />
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Upload Template</DialogTitle>
                <DialogDescription>Upload a PDF file to create a new template.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tpl-name">Template Name</Label>
                  <Input
                    id="tpl-name"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="e.g. Certificate of Test"
                    required
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>PDF File</Label>
                  <div
                    className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
                    onClick={() => fileRef.current?.click()}
                    data-testid="file-upload-zone"
                  >
                    <Upload size={24} className="mx-auto text-slate-400 mb-2" strokeWidth={1.5} />
                    <p className="text-sm text-slate-600">
                      {uploadFile ? uploadFile.name : "Click to select a PDF file"}
                    </p>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      data-testid="file-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={uploading || !uploadFile || !uploadName.trim()} data-testid="upload-submit-button">
                    {uploading ? "Uploading..." : "Upload Template"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        ) : templates.length === 0 ? (
          <Card className="border-dashed" data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FileText size={28} className="text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-700 mb-1">No templates yet</h3>
              <p className="text-sm text-slate-500 mb-4">Upload your first PDF template to get started</p>
              <Button onClick={() => setUploadOpen(true)} className="gap-2" data-testid="empty-upload-button">
                <Plus size={16} strokeWidth={1.5} />
                Upload Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((tpl) => (
              <Card
                key={tpl.id}
                className="group hover:shadow-md hover:border-slate-300 transition-all"
                data-testid={`template-card-${tpl.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                        <FileText size={20} className="text-amber-600" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-sm truncate" data-testid={`template-name-${tpl.id}`}>
                          {tpl.name}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {tpl.page_count} page{tpl.page_count !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`template-menu-${tpl.id}`}>
                          <MoreVertical size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/templates/${tpl.id}`} className="gap-2">
                            <ExternalLink size={14} /> Open Editor
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(tpl.id)} className="text-red-600 gap-2" data-testid={`delete-template-${tpl.id}`}>
                          <Trash2 size={14} /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Clock size={12} strokeWidth={1.5} />
                    <span>{new Date(tpl.created_at).toLocaleDateString()}</span>
                    <span className="mx-1">|</span>
                    <span>{tpl.variables?.length || 0} variable{(tpl.variables?.length || 0) !== 1 ? "s" : ""}</span>
                  </div>

                  <Link to={`/templates/${tpl.id}`}>
                    <Button variant="secondary" className="w-full gap-2 text-xs h-8" data-testid={`open-editor-${tpl.id}`}>
                      <ExternalLink size={12} strokeWidth={1.5} />
                      Open Editor
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
